import type { FC, HTMLAttributes, ReactNode, RefObject } from 'react'
import { Suspense, useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useEvent } from 'react-use'
import { useMyInfo } from 'src/services/apollo/auth'
import type {
  ArtworkPreviewFragment,
  ArtworkType,
} from 'src/services/apollo/generated/graphql'
import { OrderOption } from 'src/services/helpers/orderOptionParams'
import { $globalSearching } from 'src/stores/signal'
import { cx } from 'src/utils/cx'
import { useIsMobile } from 'src/utils/hooks/useBreakpoint'
import inView from 'src/utils/inView'
import type { BaseArtworkItemPreviewCardProps } from '../Artwork/BaseArtworkItemPreviewCard'
import { type TrackableArtwork, trackArtwork } from '../ArtworkDetail/common'
import Empty from '../Empty'
import LoadingPage from '../LoadingPage'
import ScrollTop from '../ScrollTop'
import StatefulArtworkGrid from './StatefulArtworkGrid'
import type { ArtworkFeedResult } from './useArtworkFeed'
import { useRecommendedArtworkFeed } from './useArtworkFeed'

// Kept for consumers that use these types as "bags of query params"
// (contestArtworkTypes.ts, src/utils/artwork.ts)
export interface ArtworkInfiniteScrollProps {
  tag?: string
  tackId?: string
  q?: string
  relevantArtworkId?: string
  order?: string
  type?: ArtworkType
  i2vPro?: boolean
  challenge?: number
  authorId?: string
  pageSize?: number
  extraArgs?: Record<string, any>

  /** To be merged with other tracking args inside BaseArtworkInifiteScroll */
  trackingArgs?: BaseArtworkItemPreviewCardProps['trackingArgs']

  /**
   * Enable tracking for image recommendations
   */
  enableTracking?: boolean

  /**
   * Show dislike button in card corner
   */
  showDislike?: boolean

  showDislikeOverlay?: boolean

  /**
   * Scroll container for `scroll` event handler
   * Default is window
   */
  scrollContainer?: Element | null
}

export interface ArtworkInfiniteScrollContentProps {
  trackingArgs?: BaseArtworkItemPreviewCardProps['trackingArgs']
  /**
   * NOTE: Manga is kept for compatibility reasons
   * Consider dropping Manga completely in the future
   */
  artworks: ArtworkPreviewFragment[]
  loadMore: () => void
  onArtworkClick: (artwork: ArtworkPreviewFragment) => any
  loading: boolean
  lastElementRef: RefObject<HTMLDivElement | null>
  queryParams: {
    q?: string
    order?: string
  }

  /**
   * Enable tracking for image recommendations
   */
  enableTracking?: boolean

  /**
   * Show dislike button in card corner
   */
  showDislike?: boolean

  showDislikeOverlay?: boolean
}

export interface BaseArtworkInfiniteScrollProps {
  feedResult: ArtworkFeedResult
  // For empty-state copy
  q?: string
  order?: string
  // For curPage tracking calculation
  pageSize?: number

  trackingArgs?: BaseArtworkItemPreviewCardProps['trackingArgs']
  scrollContainer?: Element | null
  showDislikeOverlay?: boolean

  renderLoading?: () => ReactNode
  renderContent: (props: ArtworkInfiniteScrollContentProps) => ReactNode
  renderEmpty?: () => ReactNode
  containerProps?: HTMLAttributes<HTMLDivElement>
  showSpeedDial?: boolean
  onInitialLoad?: () => void
}

const BaseArtworkInfiniteScroll: FC<BaseArtworkInfiniteScrollProps> = ({
  feedResult,
  q,
  order,
  pageSize = 24,
  renderEmpty,
  renderLoading = () => <LoadingPage />,
  renderContent,
  containerProps,
  showSpeedDial = true,
  trackingArgs,
  scrollContainer = typeof window !== 'undefined' ? window : null,
  showDislikeOverlay,
  onInitialLoad,
}) => {
  const { t } = useTranslation(['artwork', 'common'])
  const {
    artworks,
    loading: loadingArtworks,
    loadingMore,
    hasNext,
    feedUuid,
    loadMore,
  } = feedResult

  const loadingMoreArtworks = loadingMore || loadingArtworks

  /**
   * Store latest loadMore in a ref so the scroll handler (bound via
   * useEvent/useEffect) always calls the freshest version and never
   * suffers from stale-closure issues.
   */
  const loadMoreRef = useRef(loadMore)
  loadMoreRef.current = loadMore

  const curPage =
    artworks.length === 0 ? undefined : Math.ceil(artworks.length / pageSize)
  const onArtworkClick = useCallback(
    async (artwork: TrackableArtwork) => {
      trackArtwork(
        'image_click',
        artwork,
        { ...trackingArgs, curPage },
        { destinations: ['getway'] },
      )
    },
    [curPage, trackingArgs],
  )

  const mergedTrackingArgs = useMemo(() => {
    return {
      ...trackingArgs,
      feedUuid,
    }
  }, [trackingArgs, feedUuid])

  const initialLoadFired = useRef(false)
  useEffect(() => {
    if (!loadingArtworks && artworks.length > 0 && !initialLoadFired.current) {
      initialLoadFired.current = true
      onInitialLoad?.()
    }
  }, [loadingArtworks, artworks.length, onInitialLoad])

  /** Replicating previous behavior, not sure still useful or not */
  useEffect(() => {
    $globalSearching.next(loadingMoreArtworks)
  }, [loadingMoreArtworks])

  const lastElementRef = useRef<HTMLDivElement>(null)

  /**
   * Stable scroll handler — reads from refs so it never goes stale,
   * even though useEvent (react-use) rebinds via async useEffect.
   */
  const scrollHandler = useCallback(() => {
    if (
      lastElementRef.current &&
      inView(lastElementRef.current, { offset: -500 })
    ) {
      loadMoreRef.current()
    }
  }, [])

  useEvent('scroll', scrollHandler, scrollContainer, { passive: true })

  /**
   * When data changes (fetchMore completed + cache merged), re-check the
   * scroll position in case the user kept scrolling while the page was in
   * flight. The duplicate-fetch guard for the Apollo path is owned by
   * `useArtworkFeed` itself.
   */
  useEffect(() => {
    if (hasNext) {
      scrollHandler()
    }
  }, [artworks, hasNext, scrollHandler])

  /** Stable reference to reduce re-render */
  const queryParams = useMemo(
    () => ({
      order,
      q,
    }),
    [order, q],
  )

  const content = loadingArtworks ? (
    renderLoading()
  ) : artworks.length === 0 ? (
    renderEmpty ? (
      renderEmpty()
    ) : (
      <Suspense>
        <DefaultEmptyComponent
          order={order}
          q={q}
          trackingArgs={mergedTrackingArgs}
          showDislikeOverlay={showDislikeOverlay}
        />
      </Suspense>
    )
  ) : (
    renderContent({
      artworks,
      loadMore,
      onArtworkClick,
      loading: loadingArtworks,
      lastElementRef,
      queryParams,
      trackingArgs: mergedTrackingArgs,
    })
  )

  return (
    <>
      <div
        {...containerProps}
        className={cx(
          'w-full flex flex-wrap justify-center mb-8 min-h-screen',
          containerProps?.className,
        )}
      >
        {content}
        {!hasNext && artworks.length > 0 && !loadingMoreArtworks && (
          <div className="text-active font-bold text-3xl w-full text-center">
            {t('common:list-reached-the-end')}
          </div>
        )}
      </div>
      {hasNext && (
        <div className="w-full flex justify-center mb-8">
          <LoadingPage fullHeight />
        </div>
      )}

      {showSpeedDial && <SpeedDialBtns />}
    </>
  )
}

const DefaultEmptyComponent = (props: {
  order?: string
  q?: string
  trackingArgs?: BaseArtworkItemPreviewCardProps['trackingArgs']
  showDislikeOverlay?: boolean
}) => {
  const { order, q, trackingArgs, showDislikeOverlay } = props
  const { data: userInfo } = useMyInfo()
  const { t } = useTranslation(['artwork'])
  return (
    <>
      <Empty
        description={
          order === OrderOption.followingNews
            ? userInfo
              ? t('artwork:search-results.empty-no-following')
              : t('artwork:search-results.empty-no-login')
            : q
              ? t('artwork:search-results.empty-with-keyword', { keyword: q })
              : t('artwork:search-results.empty-without-keyword')
        }
      />
      <section className="w-full">
        <h3 className="mb-4 text-xl font-bold">
          {t('artwork:search-results.recommendation-section.heading')}
        </h3>

        <Suspense>
          <DefaultEmptyArtworkGrid
            trackingArgs={trackingArgs}
            showDislikeOverlay={showDislikeOverlay}
          />
        </Suspense>
      </section>
    </>
  )
}

const DefaultEmptyArtworkGrid = (props: {
  trackingArgs?: BaseArtworkItemPreviewCardProps['trackingArgs']
  showDislikeOverlay?: boolean
}) => {
  const recFeed = useRecommendedArtworkFeed({ first: 24 })
  return (
    <StatefulArtworkGrid
      feedResult={recFeed}
      showDislike
      showDislikeOverlay={props.showDislikeOverlay}
      enableTracking
      trackingArgs={props.trackingArgs}
      renderEmpty={() => null}
    />
  )
}

const SpeedDialBtns = () => {
  const isMobile = useIsMobile()

  return (
    <>
      <ScrollTop style={{ bottom: isMobile ? 76 : undefined }} />
    </>
  )
}
export default BaseArtworkInfiniteScroll
